import { Editor, Path, Range, Text, Transforms } from 'slate';

import { AlineTransforms, Queries } from '../internal';

export function withMarkers<T extends Editor>(editor: T): T {
  const { delete: deleteText } = editor;

  editor.delete = options => {
    const at = Queries.locationToDelete(editor, options);

    if (!at || Path.isPath(at)) {
      deleteText(options);
      return;
    }

    const { adjustedRange, markerInfo } = Queries.getMarkersInRange(editor, at);

    /**
     * As per Word's behavior, do not delete unpaired markers. Only delete collapsed markers if the
     * selection is expanded. Note that we use editor.selection rather than options.at for this
     * since at might be passed in as a range even when the selection is collapsed.
     */
    const keepCollapsedMarkers = editor.selection && Range.isCollapsed(editor.selection);

    const markersToKeep = markerInfo
      .filter(marker => !marker.paired || (keepCollapsedMarkers && marker.collapsed))
      .flatMap(marker => marker.nodes);

    const start = Range.start(adjustedRange);
    const rangeRef = Editor.rangeRef(editor, adjustedRange, { affinity: 'forward' });

    AlineTransforms.withoutNormalizing(editor, () => {
      if (markersToKeep.length) {
        /**
         * Reinsert markers before the deletion range, so that the cursor ends up after them.
         * Always split the text node at the insertion point to ensure we have a valid point after
         * the inserted markers.
         */
        Transforms.splitNodes(editor, { always: true, at: start, match: Text.isText });
        Transforms.insertNodes(editor, markersToKeep, { at: start });
      }

      const at = rangeRef.unref();
      if (!at) return;

      deleteText({ ...options, at });
    });
  };

  return editor;
}
